import React from 'react'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { ChartContainer } from '@/components/ui/chart'
import StickyYAxisLabels from './StickyYAxisLabels'
import StickyReferenceLineLabels from './StickyReferenceLineLabels'
import { DEFAULT_CHART_MARGIN } from './chart-axis-utils'

const ScrollableChart = ({
  children,
  config,
  width,
  height = 600,
  yAxis = {
    min: 0,
    max: 100,
    tickCount: 8,
    formatter: (v) => v,
    showPercentage: false,
  },
  referenceLines = [],
  className,
  gutterWidth = 60, // Default width for label gutters
  scrollToEnd = false,
  /** 为 true 时图表宽度等于视口宽度，不横向滚动，所有数据压缩在一屏内 */
  fitViewport = false,
  /** 须与 ComposedChart margin 一致，用于左侧 Y 轴标签定位 */
  chartMargin = DEFAULT_CHART_MARGIN,
}) => {
  const scrollAreaRef = React.useRef(null)
  const scrollWrapperRef = React.useRef(null)
  const [viewportWidth, setViewportWidth] = React.useState(0)

  React.useEffect(() => {
    if (!fitViewport || !scrollWrapperRef.current) return
    const el = scrollWrapperRef.current
    const ro = new ResizeObserver(() => {
      setViewportWidth(el.clientWidth)
    })
    ro.observe(el)
    setViewportWidth(el.clientWidth)
    return () => ro.disconnect()
  }, [fitViewport])

  const effectiveWidth =
    fitViewport && viewportWidth > 0 ? viewportWidth : width

  React.useEffect(() => {
    if (scrollToEnd && scrollAreaRef.current && !fitViewport) {
      const viewport = scrollAreaRef.current.querySelector(
        '[data-radix-scroll-area-viewport]',
      )
      if (viewport) {
        viewport.scrollLeft = viewport.scrollWidth
      }
    }
  }, [scrollToEnd, width, fitViewport])

  return (
    <div className="flex w-full items-start">
      {/* Left Gutter for Y-Axis Labels */}
      <div
        className="relative flex-shrink-0"
        style={{ width: gutterWidth, height }}
      >
        <StickyYAxisLabels
          yMin={yAxis.min}
          yMax={yAxis.max}
          chartHeight={height}
          tickCount={yAxis.tickCount}
          ticks={yAxis.ticks}
          tickFormatter={yAxis.formatter}
          showPercentage={yAxis.showPercentage}
          margin={chartMargin}
          leftPosition={0}
          className="w-full pr-2"
        />
      </div>

      {/* Scrollable Chart Area */}
      <div ref={scrollWrapperRef} className="flex-1 min-w-0" style={{ height }}>
        <ScrollArea ref={scrollAreaRef} className="h-full w-full">
          <ChartContainer
            config={config}
            className="min-w-full h-full aspect-auto [&_.recharts-responsive-container]:!h-full"
            style={{ width: effectiveWidth, height }}
          >
            {children}
          </ChartContainer>
          {!fitViewport && <ScrollBar orientation="horizontal" />}
        </ScrollArea>
      </div>

      {/* Right Gutter for Reference Line Labels */}
      {referenceLines.length > 0 ? (
        <div
          className="relative flex-shrink-0"
          style={{ width: gutterWidth, height }}
        >
          <StickyReferenceLineLabels
            referenceLinesData={referenceLines}
            yMin={yAxis.min}
            yMax={yAxis.max}
            chartHeight={height}
            showPercentage={yAxis.showPercentage}
            margin={chartMargin}
            rightOffset={0}
            className="w-full pl-2"
          />
        </div>
      ) : // Optional: Render empty gutter to maintain symmetry or just nothing
      null}
    </div>
  )
}

export default ScrollableChart
